import React from "react";
import { Link, useLocation } from "react-router-dom";
import { Globe, Search as SearchIcon, User, LogOut, Home as HomeIcon, ChevronDown } from "lucide-react";
import { useAuth } from "../../context/MyCinemaContext";
import { useSearch } from "../../context/SearchContext";
import { useMalayalamLang } from "../../context/MalayalamLangContext";
import { C, NAV, Logo } from "./mlShared";

const TopLink = ({ icon: Icon, label, onClick, testid }) => (
  <button onClick={onClick} data-testid={testid}
    className="inline-flex items-center gap-1.5 text-[13px] font-medium transition-colors hover:opacity-70"
    style={{ color: C.ink }}>
    <Icon className="h-4 w-4" style={{ color: C.gold }} /> {label}
  </button>
);

export const MalayalamLayout = ({ children }) => {
  const { setAuthOpen, user, signOut } = useAuth();
  const { setOpen } = useSearch();
  const { lang, setLang } = useMalayalamLang();
  const loc = useLocation();

  return (
    <div className={`min-h-screen ${lang === "ml" ? "ml-lang-ml" : ""}`} style={{ background: C.bg, color: C.ink }} data-testid="ml-edition">
      {/* Top bar */}
      <div className="border-b" style={{ borderColor: C.line, background: "#FCF8EF" }}>
        <div className="mx-auto flex max-w-[1500px] items-center justify-between px-6 py-2.5">
          <div className="flex items-center gap-2 text-[13px] font-semibold">
            <button onClick={() => setLang("ml")} style={{ color: lang === "ml" ? C.gold : C.navy }} data-testid="ml-lang-malayalam">മലയാളം</button>
            <span style={{ color: C.line }}>|</span>
            <button onClick={() => setLang("en")} style={{ color: lang === "en" ? C.gold : C.navy }} data-testid="ml-lang-english">English</button>
          </div>
          <div className="flex items-center gap-6">
            <TopLink icon={Globe} label="Translate" testid="ml-translate-btn" onClick={() => setLang(lang === "ml" ? "en" : "ml")} />
            <TopLink icon={SearchIcon} label="Search" testid="ml-search-btn" onClick={() => setOpen(true)} />
            {user ? (
              <div className="flex items-center gap-3">
                <span className="inline-flex items-center gap-1.5 text-[13px] font-semibold" style={{ color: C.navy }} data-testid="ml-user-name">
                  <User className="h-4 w-4" style={{ color: C.gold }} /> {user.name || user.email}
                </span>
                <TopLink icon={LogOut} label="Sign Out" testid="ml-signout-btn" onClick={signOut} />
              </div>
            ) : (
              <TopLink icon={User} label="Sign In" testid="ml-signin-btn" onClick={() => setAuthOpen(true)} />
            )}
          </div>
        </div>
      </div>

      {/* Header */}
      <div className="mx-auto flex max-w-[1500px] items-center justify-between gap-4 px-6 py-5">
        <Link to="/malayalam" className="flex items-center gap-4">
          <Logo />
          <div>
            <div className="leading-none" style={{ fontFamily: "Georgia, 'Times New Roman', serif" }}>
              <span className="text-[42px] font-bold" style={{ color: C.navy }}>MalayalaCinema</span>
              <span className="text-[42px] font-bold" style={{ color: C.gold }}>.com</span>
            </div>
            <div className="mt-1 flex items-center gap-3">
              <span style={{ color: C.gold }}>—</span>
              <span className="text-sm tracking-[0.2em]" style={{ color: C.muted }}>THE HOME OF MALAYALAM CINEMA</span>
              <span style={{ color: C.gold }}>—</span>
            </div>
          </div>
        </Link>
        <div className="hidden text-sm md:block" style={{ color: C.navy }}>
          An <span className="font-semibold" style={{ color: C.gold }}>IndiasCinema.com</span> Network Edition
        </div>
      </div>

      {/* Nav (tabs kept in English by design) */}
      <nav className="border-y" style={{ borderColor: C.line, background: "#FCF8EF" }}>
        <div className="mx-auto flex max-w-[1500px] items-center px-6">
          <Link to="/malayalam" className="mr-2 flex h-11 w-12 items-center justify-center rounded-t-md" style={{ background: C.gold }} data-testid="ml-nav-home-icon">
            <HomeIcon className="h-5 w-5 text-white" />
          </Link>
          <div className="flex flex-wrap items-center">
            {NAV.map((item) => {
              const active = item.to === "/malayalam" ? loc.pathname === "/malayalam" : loc.pathname === item.to;
              return (
                <Link key={item.label} to={item.to}
                  data-testid={`ml-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="inline-flex items-center gap-1 whitespace-nowrap px-3 py-4 text-[13px] font-semibold uppercase tracking-wide transition-colors hover:opacity-80"
                  style={{ color: active ? C.gold : C.navy }}>
                  {item.label}{item.caret && <ChevronDown className="h-3.5 w-3.5" />}
                </Link>
              );
            })}
          </div>
        </div>
      </nav>

      {children}

      {/* Footer */}
      <footer className="border-t" style={{ borderColor: C.line, background: "#FCF8EF" }}>
        <div className="mx-auto flex max-w-[1500px] flex-col items-center gap-2 px-6 py-8 text-center">
          <div className="flex items-center gap-1 text-xl font-semibold" style={{ fontFamily: "Georgia, serif", color: C.navy }}>
            MalayalaCinema<span style={{ color: C.gold }}>.com</span>
          </div>
          <p className="text-sm" style={{ color: C.muted }}>
            An <span className="font-semibold" style={{ color: C.gold }}>IndiasCinema.com</span> Network Edition
          </p>
          <p className="mt-2 text-xs" style={{ color: C.muted }}>© 2026 MalayalaCinema.com</p>
        </div>
      </footer>
    </div>
  );
};
